import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const fmtUsd = (v: number) => `$${v.toFixed(2)}`;

/**
 * AWS spend (Cost Explorer) — a distinct cost-source from the upstream API
 * providers. Data lags ~24-48h (the most recent day is partial), so it's shown
 * separately from the per-provider balances and labeled accordingly.
 */
export function AwsCostsPanel() {
  const [period, setPeriod] = useState<'mtd' | '30d'>('mtd');
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.adminProviders.awsCosts(period),
    queryFn: () => api.adminGetAwsCosts(period),
  });
  const report = data?.report;
  const max = Math.max(1, ...(report?.byService ?? []).map((s) => s.amountUsd));

  return (
    <div className="rounded-lg border border-border bg-card p-4 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold">AWS spend</h2>
        <div className="flex gap-1 text-xs">
          {(['mtd', '30d'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={`px-2 py-1 rounded ${period === p ? 'bg-primary text-primary-foreground' : 'border border-border'}`}
            >
              {p === 'mtd' ? 'Month-to-date' : 'Last 30 days'}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Cost Explorer — data lags ~24-48h; the most recent day is partial.
        {report?.dataAsOf ? ` Data through ${report.dataAsOf}.` : ''}
      </p>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : !report?.ok ? (
        <p className="text-sm text-muted-foreground">
          AWS cost data unavailable
          {report?.error ? ` (${report.error})` : ''}. Cost Explorer must be enabled and the task
          role granted <code>ce:GetCostAndUsage</code>.
        </p>
      ) : (
        <>
          <div className="text-2xl font-bold mb-3">{fmtUsd(report.totalUsd)}</div>
          <div className="space-y-1">
            {report.byService.slice(0, 10).map((s) => (
              <div key={s.service} className="flex items-center gap-2 text-sm">
                <div className="w-40 truncate text-muted-foreground" title={s.service}>
                  {s.service}
                </div>
                <div className="flex-1 bg-muted rounded h-3 overflow-hidden">
                  <div
                    className="bg-primary h-full"
                    style={{ width: `${(s.amountUsd / max) * 100}%` }}
                  />
                </div>
                <div className="w-20 text-right tabular-nums">{fmtUsd(s.amountUsd)}</div>
              </div>
            ))}
            {report.byService.length === 0 && (
              <p className="text-sm text-muted-foreground">No spend recorded for this period.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
